import { useEffect } from 'react';

const SUPPORT_SCRIPT_ID = 'rex-support-embed';
const SUPPORT_SCRIPT_SRC = 'https://rex-support-os.vercel.app//embed.js';
const NPS_SCRIPT_ID = 'rex-nps-embed';
const NPS_SCRIPT_SRC = 'https://rex-support-os.vercel.app/nps-embed.js';

type RexSupportUser = { email: string; name: string; id: string };

declare global {
  interface Window {
    RexSupport?: {
      user?: RexSupportUser;
      identify?: (user: RexSupportUser) => void;
    };
  }
}

/**
 * Load the NPS embed once. Only called from identifyRexSupport (post-login),
 * so the survey never appears to anonymous / logged-out visitors.
 */
function mountNpsWidget() {
  if (document.getElementById(NPS_SCRIPT_ID)) return;
  const s = document.createElement('script');
  s.id = NPS_SCRIPT_ID;
  s.src = NPS_SCRIPT_SRC;
  s.defer = true;
  s.dataset.rexNpsProduct = 'payup';
  document.head.appendChild(s);
}

/**
 * Push user identity to the Rex Support + NPS widgets. Safe to call before
 * embed.js has loaded — we set window.RexSupport.user, which embed.js reads
 * at startup (and also polls for). If embed.js has already loaded,
 * identify() forwards the user into the iframe via postMessage.
 *
 * Call this from the login flow as soon as the user's email is known. This is
 * also where the NPS widget is mounted, so NPS only ever surveys logged-in
 * users.
 */
export function identifyRexSupport(user: RexSupportUser) {
  window.RexSupport = { ...(window.RexSupport || {}), user };
  window.RexSupport.identify?.(user);
  mountNpsWidget();
}

export default function RexSupportWidget() {
  useEffect(() => {
    // Mount the support-chat script regardless of auth state so logged-out
    // visitors can still reach support. embed.js polls window.RexSupport.user
    // for 60 s and exposes RexSupport.identify(), so identity can arrive after
    // the script loads. The NPS script is deferred to identifyRexSupport
    // (post-login) so anonymous visitors are never surveyed.
    if (!document.getElementById(SUPPORT_SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SUPPORT_SCRIPT_ID;
      s.src = SUPPORT_SCRIPT_SRC;
      s.defer = true;
      s.dataset.rexSupportProduct = 'payup';
      document.head.appendChild(s);
    }
  }, []);

  return null;
}
